import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Icon } from "@/components/ui/icon";
import type { BranePlatform } from "@/lib/brane-open";
import { PlayTab, StoreTab, visitorPlatform } from "./StoreTab";
import { VERIFY_EXAMPLE } from "./demo";
import { nouns } from "@/components/sheet/nouns";

/*
  One closing block, in the order the primary visitor needs it: it works now in
  the browser she is holding (first, and stated positively); verification
  shown as the act rather than claimed; the one action. Beside them, the three
  ways in as an index, web first because it is the one she is already using:
  each platform's name at row scale, a line, and its store tab, with a cut
  vermilion slip on the row for the device she is holding. Then a short note from the one person who
  builds it, set the way a maker signs a sheet: name on the left, words on the
  right. The page's one thread comes down into the closing line and is woven
  through it (Thread.tsx), so the page ends where it began. No figures anywhere (PRODUCT.md, Claims Policy).
*/
export function Close() {
  const { t } = useTranslation();
  const here: BranePlatform = visitorPlatform();
  return (
    <section id="start" className="sh-close" aria-labelledby="sh-close-title">
      <div className="sh-close__head">
        <h2 id="sh-close-title" className="sh-close__title">
          {t("landing.close.title")}
        </h2>
      </div>
      <div className="sh-wrap">
        <div className="sh-close__grid">
          <div className="sh-close__main">
            <p className="sh-body">{nouns(t("landing.close.body"))}</p>
            <div className="sh-verify">
              <span className="sh-verify__label">
                <span className="sh-verify__addr">{VERIFY_EXAMPLE}</span>
                <span className="sh-verify__mark">
                  <Icon glyph={Check} scale="inline" />
                  {t("landing.close.verified")}
                </span>
              </span>
              <p className="sh-verify__text">{t("landing.close.verify")}</p>
              <p className="sh-verify__trust">{t("cta.desc")}</p>
            </div>
            <Link to="/signup" className="sh-plate sh-close__plate" data-cta>
              {t("hero.cta")}
            </Link>
          </div>
          <ul className="sh-platforms" aria-label={t("landing.close.platforms")}>
            <Platform name={t("landing.close.webName")} line={t("landing.close.web")} here={here === "desktop"} />
            <Platform name="iPhone" line={t("landing.close.ios")} here={here === "ios"}>
              <StoreTab />
            </Platform>
            <Platform name="Android" line={t("landing.close.android")} here={here === "android"}>
              <PlayTab />
            </Platform>
          </ul>
        </div>

        <figure className="sh-note">
          <figcaption className="sh-note__sign">
            <span className="sh-note__name">YEP</span>
            <a href="https://www.yepchaos.com" target="_blank" rel="noreferrer" className="sh-textlink">
              yepchaos.com
            </a>
            <a href="mailto:yep@astring.app" className="sh-textlink">
              yep@astring.app
            </a>
          </figcaption>
          <blockquote className="sh-note__words">
            <p>{t("landing.close.note")}</p>
          </blockquote>
        </figure>
      </div>
    </section>
  );
}

/* One row of the platform index. The web row has no tab: the plate is its action. */
function Platform({
  name,
  line,
  here,
  children,
}: {
  name: string;
  line: string;
  here: boolean;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <li className="sh-platform" data-here={here || undefined}>
      <span className="sh-platform__head">
        <span className="sh-platform__name">{name}</span>
        {here && <span className="sh-platform__here">{t("landing.close.here")}</span>}
      </span>
      <p className="sh-platform__line">{line}</p>
      {children}
    </li>
  );
}
